import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
  type QueryClient,
} from "@tanstack/react-query";
import { useAuthStore } from "@/modules/auth";
import { api } from "../client";

// Mail Hub (docs/plans/mail-hub-design.md). Mailbox admin = mail.manage
// (SA / TA). Inbox = mail.use; what a user sees is decided server-side.

export type MailboxKind = "personal" | "shared";
/** Where the mailbox lives (apps.mail.models.MailProvider). */
export type MailboxProvider = "workman" | "hostinger";
export type MailTriageState = "needs_workunit" | "linked" | "not_work";
export type MailboxStatus = "active" | "inactive";
/** Where a conversation sits: Inbox / Archive / Trash are per person, Spam is shared. */
/** "folder" = one of your folders (personal or a mailbox's folder). */
export type MailFolder = "inbox" | "archive" | "trash" | "spam" | "folder";
export type MailSpamState = "" | "spam" | "not_spam";
export type MailListFolder = "" | "sent" | "all" | "archive" | "trash" | "spam";
export type MailBulkAction = "archive" | "trash" | "inbox" | "spam" | "not_spam" | "read" | "unread" | "folder";

export interface MailAddress {
  name: string;
  address: string;
}

export interface MailboxMemberRow {
  id: string;
  userId: string;
  userName: string;
  userEmail: string;
  userRole: string;
  status: MailboxStatus;
  createdAt: string | null;
}

export interface MailFolderState {
  folder: string;
  lastUid: number;
  lastSyncedAt: string | null;
  lastError: string;
  lastErrorAt: string | null;
  consecutiveFailures: number;
}

export interface MailboxRow {
  id: string;
  address: string;
  displayName: string;
  kind: MailboxKind;
  backfillDays: number;
  provider: MailboxProvider;
  /** A password is stored (Hostinger). The password itself is never sent. */
  hasPassword: boolean;
  passwordSetAt: string | null;
  /** Set when the provider rejected the password: background sync is paused. */
  authFailedAt: string | null;
  authError: string;
  /** "Import older mail": the latest run. */
  import: {
    since: string | null;
    startedAt: string | null;
    finishedAt: string | null;
    /** Copies stored so far. */
    count: number;
    /** Messages to fetch (0 while still counting on the mail server). */
    total: number;
    /** Of those, fetched so far. */
    done: number;
    /** Server folder being read now ("INBOX", "Sent", "Junk"). */
    folder: string;
    error: string;
    running: boolean;
    /** Filed as old mail (Not work, "Imported history"); it still lands in Inbox / Sent / Spam. */
    asHistory: boolean;
  };
  status: MailboxStatus;
  members: MailboxMemberRow[];
  memberCount: number;
  sync: { lastSyncedAt: string | null; hasErrors: boolean; folders: MailFolderState[] };
  deactivatedAt: string | null;
  deactivationReason: string;
  createdAt: string | null;
  updatedAt: string | null;
}

export interface MyMailbox {
  id: string;
  address: string;
  displayName: string;
  kind: MailboxKind;
  isMember: boolean;
  /** Received conversations not filed yet (shown in the Inbox). */
  needsWorkunitCount: number;
  /** Sent from this mailbox outside Workman, not filed yet (only in Sent). */
  sentNeedsWorkunitCount: number;
}

export interface MailWorkUnitRef {
  id: string;
  ctrlNo: string;
  /** False = the linked tag is shown but this user cannot open the WorkUnit. */
  visible: boolean;
  /** Reading pane only, and only when `visible`. */
  clientName?: string;
  projectName?: string;
  lifecycleState?: string;
}

export interface MailThreadRow {
  id: string;
  subject: string;
  triageState: MailTriageState;
  notWorkReason: string;
  workUnit: MailWorkUnitRef | null;
  messageCount: number;
  lastMessageAt: string | null;
  lastFrom: MailAddress | null;
  snippet: string;
  hasAttachments: boolean;
  /** The newest message this user sent in it (Sent view, Gmail style). */
  lastSent: { to: MailAddress[]; at: string | null; snippet: string } | null;
  inJunk: boolean;
  mailboxes: string[];
  /** Something new from someone else since this user last opened it. */
  isUnread: boolean;
  /** Search only: the words around the first hit (may be in an older message). */
  matchSnippet: string;
}

export interface MailAttachmentRow {
  id: string;
  filename: string;
  contentType: string;
  sizeBytes: number;
  isInline: boolean;
  /** For pictures shown inside the message (cid: references). */
  contentId: string;
}

export interface MailMessageRow {
  id: string;
  direction: "inbound" | "outbound";
  from: MailAddress;
  to: MailAddress[];
  cc: MailAddress[];
  replyTo: MailAddress[];
  /** Only non-empty for the sender and SA/TA. */
  bcc: MailAddress[];
  subject: string;
  sentAt: string | null;
  sentByName: string | null;
  bodyText: string;
  bodyHtml: string;
  bodyTruncated: boolean;
  isAutoReply: boolean;
  isPrivate: boolean;
  inJunk: boolean;
  /** Owner of a receiving mailbox (or SA/TA) may hide it from the WorkUnit team. */
  canMarkPrivate: boolean;
  /** Workman holds the original (.eml); SA / TA get every header, others a copy without delivery / Bcc headers. */
  canDownloadOriginal?: boolean;
  /** You sent it (from Workman, or it is in your mailbox's Sent). */
  sentByMe?: boolean;
  /** Incoming mail: the receiving server confirmed (true) / could not confirm (false) the sender. */
  senderVerified?: boolean | null;
  /** Which of the current user's mailboxes received it. */
  inMyMailboxIds: string[];
  attachments: MailAttachmentRow[];
}

export interface MailThreadDetail {
  id: string;
  subject: string;
  triageState: MailTriageState;
  notWorkReason: string;
  triagedAt: string | null;
  workUnit: MailWorkUnitRef | null;
  access: ("mailbox" | "workunit" | "share" | "admin")[];
  /** Link / Not work / Hand over allowed for this user. */
  canTriage: boolean;
  /** Moving a LINKED conversation to Not work is SA/TA only. */
  canMarkNotWorkWhenLinked: boolean;
  /** For this user. */
  folder: MailFolder;
  /** Your folders (personal / your mailboxes' shared ones) this conversation is in. */
  folders: MailFolderRow[];
  spamState: MailSpamState;
  /** Archive / Trash need the conversation filed first. */
  canPutAway: boolean;
  canMarkSpam: boolean;
  messages: MailMessageRow[];
}

export interface TeamBacklogRow {
  userId: string;
  userName: string;
  unlinkedCount: number;
  oldestAt: string | null;
}

/** Who wrote last: "awaiting_us" = the other side (they wait for our reply). */
export type MailReplyState = "awaiting_us" | "awaiting_them";

/** Attachment kinds the list can filter on (server FILE_TYPES). */
export type MailFileType = "pdf" | "sheet" | "doc" | "image" | "archive";

export interface MailThreadListParams {
  mailbox?: string;
  /** Conversations filed under this WorkUnit (WorkUnit Mail tab, or a filter). */
  workUnit?: string;
  triage?: MailTriageState | "";
  /** Every word must match; "quoted phrases" stay together. Also understands
   *  from: to: subject: file: has:attachment is:unread after: before: on: -word. */
  search?: string;
  unread?: boolean;
  attachments?: boolean;
  handed?: boolean;
  reply?: MailReplyState | "";
  /** Sender name, address or domain. */
  from?: string;
  /** A To / Cc name, address or domain. */
  to?: string;
  /** Words in the subject. */
  subject?: string;
  /** Words no message of the conversation may contain. */
  without?: string;
  /** Attachment name. */
  file?: string;
  fileType?: MailFileType | "";
  client?: string;
  /** YYYY-MM-DD: a message of the conversation is dated in this range. */
  since?: string;
  until?: string;
  spam?: "" | "hide" | "only";
  sort?: "newest" | "oldest";
  /** Empty = Inbox (received). Sent, All (everything but Spam / Trash), Archive, Trash, Spam. */
  folder?: MailListFolder;
  /** mine = only your mailboxes and what was handed to you. */
  scope?: "mine" | "all";
  /** One of your folders. */
  folderId?: string;
  page?: number;
  pageSize?: number;
}

/** Status-tab totals for the same mailbox / search / WorkUnit scope. */
export interface MailTriageCounts {
  all: number;
  needsWorkunit: number;
  linked: number;
  notWork: number;
}

export const TRIAGE_COUNT_KEY: Record<MailTriageState, keyof MailTriageCounts> = {
  needs_workunit: "needsWorkunit",
  linked: "linked",
  not_work: "notWork",
};

export interface MailThreadListResponse {
  results: MailThreadRow[];
  count: number;
  page: number;
  pageSize: number;
  counts: MailTriageCounts;
}

// --- Mailbox admin (mail.manage) -------------------------------------------

export interface MailHubStatus {
  configured: boolean;
  /** Workman's own mail server (master login) is set up. */
  ownServer: boolean;
  /** The server has an encryption key, so Hostinger passwords can be stored. */
  canStorePasswords: boolean;
}

export function useMailHubStatus(enabled = true) {
  return useQuery<MailHubStatus>({
    queryKey: ["mail", "status"],
    queryFn: () => api.get<MailHubStatus>("/mail/status").then((r) => r.data),
    enabled,
  });
}

export function useMailboxes(params: { status?: string; search?: string } = {}) {
  const status = params.status ?? "";
  const search = params.search ?? "";
  return useQuery<{ results: MailboxRow[] }>({
    queryKey: ["mail", "mailboxes", { status, search }],
    queryFn: () =>
      api
        .get<{ results: MailboxRow[] }>("/mail/mailboxes", {
          params: { status: status || undefined, search: search || undefined },
        })
        .then((r) => r.data),
    // Follow running imports (progress column).
    refetchInterval: (q) => (q.state.data?.results.some((m) => m.import.running) ? 3000 : false),
  });
}

export function useMailbox(id: string | null | undefined) {
  return useQuery<MailboxRow>({
    queryKey: ["mail", "mailboxes", "detail", id],
    queryFn: () => api.get<MailboxRow>(`/mail/mailboxes/${id}`).then((r) => r.data),
    enabled: !!id,
    // Follow a running import.
    refetchInterval: (q) => (q.state.data?.import?.running ? 3000 : false),
  });
}

/** Fetch the last N days that Workman does not hold yet (runs in the background). */
export function useImportOlderMail(id: string) {
  const qc = useQueryClient();
  return useMutation<MailboxRow, unknown, { days: number; asHistory: boolean }>({
    mutationFn: (body) => api.post<MailboxRow>(`/mail/mailboxes/${id}/import`, body).then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["mail"] }),
  });
}

export interface MailboxCreateInput {
  address: string;
  displayName?: string;
  kind: MailboxKind;
  backfillDays: number;
  provider: MailboxProvider;
  /** Hostinger only. Sent once, stored encrypted, never returned. */
  password?: string;
}

/** Link form "Test connection": tries the login, saves nothing. */
export function useTestMailboxConnection() {
  return useMutation<
    { address: string; inboxMessages: number },
    unknown,
    { address: string; provider: MailboxProvider; password?: string }
  >({
    mutationFn: (body) =>
      api
        .post<{ address: string; inboxMessages: number }>("/mail/mailboxes/test-connection", body)
        .then((r) => r.data),
  });
}

export function useSetMailboxPassword(id: string) {
  const qc = useQueryClient();
  return useMutation<MailboxRow, unknown, string>({
    mutationFn: (password) =>
      api.post<MailboxRow>(`/mail/mailboxes/${id}/password`, { password }).then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["mail"] }),
  });
}

export function useCreateMailbox() {
  const qc = useQueryClient();
  return useMutation<MailboxRow, unknown, MailboxCreateInput>({
    mutationFn: (body) => api.post<MailboxRow>("/mail/mailboxes", body).then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["mail"] }),
  });
}

export function useUpdateMailbox(id: string) {
  const qc = useQueryClient();
  return useMutation<MailboxRow, unknown, Partial<Pick<MailboxCreateInput, "displayName" | "kind" | "backfillDays">>>({
    mutationFn: (body) => api.patch<MailboxRow>(`/mail/mailboxes/${id}`, body).then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["mail"] }),
  });
}

export function useDeactivateMailbox(id: string) {
  const qc = useQueryClient();
  return useMutation<MailboxRow, unknown, string>({
    mutationFn: (reason = "") =>
      api.post<MailboxRow>(`/mail/mailboxes/${id}/deactivate`, { reason }).then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["mail"] }),
  });
}

export function useActivateMailbox(id: string) {
  const qc = useQueryClient();
  return useMutation<MailboxRow, unknown, void>({
    mutationFn: () => api.post<MailboxRow>(`/mail/mailboxes/${id}/activate`, {}).then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["mail"] }),
  });
}

export function useVerifyMailbox(id: string) {
  return useMutation<{ address: string; inboxMessages: number }, unknown, void>({
    mutationFn: () =>
      api
        .post<{ address: string; inboxMessages: number }>(`/mail/mailboxes/${id}/verify`, {})
        .then((r) => r.data),
  });
}

export function useSyncMailbox(id: string) {
  const qc = useQueryClient();
  return useMutation<{ scheduled: boolean }, unknown, void>({
    mutationFn: () => api.post<{ scheduled: boolean }>(`/mail/mailboxes/${id}/sync`, {}).then((r) => r.data),
    // The sync runs in the background; refresh shortly after so the cursor /
    // error state on screen catches up.
    onSuccess: () => {
      window.setTimeout(() => void qc.invalidateQueries({ queryKey: ["mail"] }), 4000);
    },
  });
}

export function useAddMailboxMember(mailboxId: string) {
  const qc = useQueryClient();
  return useMutation<MailboxMemberRow, unknown, { userId: string }>({
    mutationFn: (body) =>
      api.post<MailboxMemberRow>(`/mail/mailboxes/${mailboxId}/members`, body).then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["mail"] }),
  });
}

export function useRemoveMailboxMember(mailboxId: string) {
  const qc = useQueryClient();
  return useMutation<MailboxMemberRow, unknown, { memberId: string; reason?: string }>({
    mutationFn: ({ memberId, reason }) =>
      api
        .post<MailboxMemberRow>(`/mail/mailboxes/${mailboxId}/members/${memberId}/remove`, {
          reason: reason ?? "",
        })
        .then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["mail"] }),
  });
}

// --- Inbox (mail.use) --------------------------------------------------------

export function useMyMailboxes() {
  return useQuery<{ results: MyMailbox[] }>({
    queryKey: ["mail", "my-mailboxes"],
    queryFn: () => api.get<{ results: MyMailbox[] }>("/mail/my-mailboxes").then((r) => r.data),
  });
}

export function useMailThreads(params: MailThreadListParams) {
  const p = {
    mailbox: params.mailbox || undefined,
    workUnit: params.workUnit || undefined,
    triage: params.triage || undefined,
    search: params.search?.trim() || undefined,
    unread: params.unread ? 1 : undefined,
    attachments: params.attachments ? 1 : undefined,
    handed: params.handed ? 1 : undefined,
    reply: params.reply || undefined,
    from: params.from?.trim() || undefined,
    to: params.to?.trim() || undefined,
    subject: params.subject?.trim() || undefined,
    without: params.without?.trim() || undefined,
    file: params.file?.trim() || undefined,
    fileType: params.fileType || undefined,
    client: params.client || undefined,
    since: params.since || undefined,
    until: params.until || undefined,
    spam: params.spam || undefined,
    sort: params.sort === "oldest" ? "oldest" : undefined,
    folder: params.folder || undefined,
    scope: params.scope === "mine" ? "mine" : undefined,
    folderId: params.folderId || undefined,
    page: params.page ?? 1,
    pageSize: params.pageSize ?? 50,
  };
  return useQuery<MailThreadListResponse>({
    queryKey: ["mail", "threads", p],
    queryFn: () => api.get<MailThreadListResponse>("/mail/threads", { params: p }).then((r) => r.data),
    placeholderData: keepPreviousData,
  });
}

export function useMailThread(id: string | null | undefined) {
  const qc = useQueryClient();
  return useQuery<MailThreadDetail>({
    queryKey: ["mail", "threads", "detail", id],
    queryFn: async () => {
      const data = await api.get<MailThreadDetail>(`/mail/threads/${id}`).then((r) => r.data);
      // Opening a conversation marks it read server-side; clear the dot in
      // every cached list without refetching them.
      setThreadUnreadInCache(qc, data.id, false);
      return data;
    },
    enabled: !!id,
  });
}

/** Flip one conversation's unread dot in every cached list page. */
export function setThreadUnreadInCache(qc: QueryClient, threadId: string, isUnread: boolean) {
  qc.setQueriesData<MailThreadListResponse>(
    { predicate: (q) => q.queryKey[0] === "mail" && q.queryKey[1] === "threads" && q.queryKey[2] !== "detail" },
    (old) =>
      old?.results
        ? { ...old, results: old.results.map((r) => (r.id === threadId ? { ...r, isUnread } : r)) }
        : old,
  );
}

export function useMarkThreadUnread() {
  const qc = useQueryClient();
  return useMutation<{ id: string; isUnread: boolean }, unknown, string>({
    mutationFn: (threadId) =>
      api.post<{ id: string; isUnread: boolean }>(`/mail/threads/${threadId}/unread`, {}).then((r) => r.data),
    onSuccess: (_d, threadId) => setThreadUnreadInCache(qc, threadId, true),
  });
}

/** Scope = what the Inbox is showing (mailbox + status tab). */
export function useMarkAllRead() {
  const qc = useQueryClient();
  return useMutation<
    { count: number },
    unknown,
    { mailbox?: string; triage?: MailTriageState | ""; folder?: MailThreadListParams["folder"]; scope?: "mine" | "all" }
  >({
    mutationFn: (v) =>
      api
        .post<{ count: number }>("/mail/threads/mark-all-read", {
          mailbox: v.mailbox || null,
          triage: v.triage || "",
          folder: v.folder || "",
          scope: v.scope || "",
        })
        .then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["mail", "threads"] }),
  });
}

// --- Triage ------------------------------------------------------------------

/** Responses return the refreshed conversation; `visible: false` when the
 *  actor no longer sees it (e.g. moved to Not work off their WorkUnit). */
type ThreadResult = MailThreadDetail | { id: string; visible: false };

function useThreadMutation<V>(threadId: string, path: string, toBody: (v: V) => unknown) {
  const qc = useQueryClient();
  return useMutation<ThreadResult, unknown, V>({
    mutationFn: (v) =>
      api.post<ThreadResult>(`/mail/threads/${threadId}/${path}`, toBody(v)).then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["mail"] }),
  });
}

/** Inbox / Archive / Trash, for you only. */
export function useMoveThread(threadId: string) {
  return useThreadMutation<{ to: "inbox" | "archive" | "trash" }>(threadId, "move", (v) => v);
}

/** One action on many conversations; ones not allowed are skipped with a reason. */
export function useBulkThreads() {
  const qc = useQueryClient();
  return useMutation<
    { done: string[]; skipped: { id: string; reason: string }[] },
    unknown,
    { ids: string[]; action: MailBulkAction; folderId?: string }
  >({
    mutationFn: (body) =>
      api
        .post<{ done: string[]; skipped: { id: string; reason: string }[] }>("/mail/threads/bulk", body)
        .then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["mail"] }),
  });
}

/** Spam / Not spam, for everyone who can see the conversation. */
export function useSetThreadSpam(threadId: string) {
  return useThreadMutation<{ spam: boolean }>(threadId, "spam", (v) => v);
}

export function useLinkThread(threadId: string) {
  return useThreadMutation<{ workUnitId: string }>(threadId, "link-workunit", (v) => v);
}

export function useMarkThreadNotWork(threadId: string) {
  return useThreadMutation<{ reason: string }>(threadId, "not-work", (v) => v);
}

export function useHandOverThread(threadId: string) {
  return useThreadMutation<{ userId: string; note?: string }>(threadId, "hand-over", (v) => v);
}

export function useSetMessagePrivacy() {
  const qc = useQueryClient();
  return useMutation<ThreadResult, unknown, { messageId: string; private: boolean }>({
    mutationFn: ({ messageId, private: priv }) =>
      api.post<ThreadResult>(`/mail/messages/${messageId}/privacy`, { private: priv }).then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["mail"] }),
  });
}

/** Unlinked-mail backlog per team member — counts and age only. */
export function useTeamBacklog() {
  return useQuery<{ results: TeamBacklogRow[] }>({
    queryKey: ["mail", "team-backlog"],
    queryFn: () => api.get<{ results: TeamBacklogRow[] }>("/mail/team-backlog").then((r) => r.data),
  });
}

// --- Sending -----------------------------------------------------------------

export interface SendOnThreadInput {
  mailboxId: string;
  replyToMessageId: string;
  mode: "reply" | "forward";
  to: string[];
  cc: string[];
  bcc: string[];
  subject: string;
  body: string;
  /** Rich text from the composer; pictures as data: URLs. */
  bodyHtml?: string;
  /** Attachments are taken from this server-side draft; it closes on send. */
  draftId?: string;
  includeQuote: boolean;
  forwardAttachmentIds: string[];
  files: File[];
}

export interface ComposeInput {
  mailboxId: string;
  /** Exactly one of: the WorkUnit it belongs to, or a Not work reason. */
  workUnitId?: string;
  notWorkReason?: string;
  to: string[];
  cc: string[];
  bcc: string[];
  subject: string;
  body: string;
  bodyHtml?: string;
  draftId?: string;
  files: File[];
}

/** Multipart: address lists / ids go comma-separated, files as `files`. */
function toForm(fields: Record<string, string | boolean>, files: File[]): FormData {
  const fd = new FormData();
  Object.entries(fields).forEach(([k, v]) => fd.append(k, String(v)));
  files.forEach((f) => fd.append("files", f, f.name));
  return fd;
}

export function useSendOnThread(threadId: string) {
  const qc = useQueryClient();
  return useMutation<ThreadResult, unknown, SendOnThreadInput>({
    mutationFn: (v) =>
      api
        .post<ThreadResult>(
          `/mail/threads/${threadId}/send`,
          toForm(
            {
              mailboxId: v.mailboxId,
              replyToMessageId: v.replyToMessageId,
              mode: v.mode,
              to: v.to.join(","),
              cc: v.cc.join(","),
              bcc: v.bcc.join(","),
              subject: v.subject,
              body: v.body,
              includeQuote: v.includeQuote,
              forwardAttachmentIds: v.forwardAttachmentIds.join(","),
              ...(v.bodyHtml ? { bodyHtml: v.bodyHtml } : {}),
              ...(v.draftId ? { draftId: v.draftId } : {}),
            },
            v.files,
          ),
        )
        .then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["mail"] }),
  });
}

export function useComposeMail() {
  const qc = useQueryClient();
  return useMutation<MailThreadDetail, unknown, ComposeInput>({
    mutationFn: (v) =>
      api
        .post<MailThreadDetail>(
          "/mail/compose",
          toForm(
            {
              mailboxId: v.mailboxId,
              ...(v.workUnitId ? { workUnitId: v.workUnitId } : { notWorkReason: v.notWorkReason ?? "" }),
              to: v.to.join(","),
              cc: v.cc.join(","),
              bcc: v.bcc.join(","),
              subject: v.subject,
              body: v.body,
              ...(v.bodyHtml ? { bodyHtml: v.bodyHtml } : {}),
              ...(v.draftId ? { draftId: v.draftId } : {}),
            },
            v.files,
          ),
        )
        .then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["mail"] }),
  });
}

// --- Drafts (server-side, private to the author) -------------------------------

export type MailDraftMode = "reply" | "reply_all" | "forward" | "new";

export interface MailDraftFileRow {
  id: string;
  filename: string;
  contentType: string;
  sizeBytes: number;
}

export interface MailDraftRow {
  id: string;
  mode: MailDraftMode;
  mailboxId: string | null;
  threadId: string | null;
  threadSubject: string;
  baseMessageId: string | null;
  workUnit: { id: string; ctrlNo: string } | null;
  notWorkReason: string;
  to: string[];
  cc: string[];
  bcc: string[];
  subject: string;
  bodyHtml: string;
  includeQuote: boolean;
  forwardAttachmentIds: string[];
  files: MailDraftFileRow[];
  updatedAt: string | null;
}

export interface MailDraftInput {
  mode?: MailDraftMode;
  mailboxId?: string | null;
  threadId?: string | null;
  baseMessageId?: string | null;
  workUnitId?: string | null;
  notWorkReason?: string;
  to?: string[];
  cc?: string[];
  bcc?: string[];
  subject?: string;
  bodyHtml?: string;
  includeQuote?: boolean;
  forwardAttachmentIds?: string[];
}

export function useMyDrafts(params: { thread?: string | null } = {}, enabled = true) {
  const thread = params.thread ?? "";
  return useQuery<{ results: MailDraftRow[] }>({
    queryKey: ["mail", "drafts", { thread }],
    queryFn: () =>
      api
        .get<{ results: MailDraftRow[] }>("/mail/drafts", { params: { thread: thread || undefined } })
        .then((r) => r.data),
    enabled,
  });
}

export function useMailDraft(id: string | null | undefined) {
  return useQuery<MailDraftRow>({
    queryKey: ["mail", "drafts", "detail", id],
    queryFn: () => api.get<MailDraftRow>(`/mail/drafts/${id}`).then((r) => r.data),
    enabled: !!id,
  });
}

export const draftApi = {
  create: (v: MailDraftInput) => api.post<MailDraftRow>("/mail/drafts", v).then((r) => r.data),
  update: (id: string, v: MailDraftInput) => api.patch<MailDraftRow>(`/mail/drafts/${id}`, v).then((r) => r.data),
  discard: (id: string) => api.post(`/mail/drafts/${id}/discard`, {}).then((r) => r.data),
  addFiles: (id: string, files: File[]) => {
    const fd = new FormData();
    files.forEach((f) => fd.append("files", f, f.name));
    return api.post<MailDraftRow>(`/mail/drafts/${id}/files`, fd).then((r) => r.data);
  },
  removeFile: (id: string, fileId: string) =>
    api.post<MailDraftRow>(`/mail/drafts/${id}/files/${fileId}/remove`, {}).then((r) => r.data),
};

// --- Signatures --------------------------------------------------------------------

export interface MailSignatureRow {
  id: string;
  /** null = default for every mailbox. */
  mailboxId: string | null;
  html: string;
}

export function useMySignatures() {
  return useQuery<{ results: MailSignatureRow[] }>({
    queryKey: ["mail", "signatures"],
    queryFn: () => api.get<{ results: MailSignatureRow[] }>("/mail/signatures").then((r) => r.data),
  });
}

export function useSaveSignature() {
  const qc = useQueryClient();
  return useMutation<{ results: MailSignatureRow[] }, unknown, { mailboxId: string | null; html: string }>({
    mutationFn: (v) => api.post<{ results: MailSignatureRow[] }>("/mail/signatures", v).then((r) => r.data),
    onSuccess: (data) => qc.setQueryData(["mail", "signatures"], data),
  });
}

/** The signature for a mailbox: its own, else the default, else none. */
export function signatureFor(sigs: MailSignatureRow[] | undefined, mailboxId: string | null | undefined): string {
  if (!sigs?.length) return "";
  return (sigs.find((s) => s.mailboxId && s.mailboxId === mailboxId) ?? sigs.find((s) => !s.mailboxId))?.html ?? "";
}

/** Save the original (.eml) Workman keeps of a message. Audited server-side. */
export async function downloadMailOriginal(messageId: string, subject: string): Promise<void> {
  const r = await api.get<Blob>(`/mail/messages/${messageId}/original`, { responseType: "blob" });
  const url = URL.createObjectURL(r.data);
  const a = document.createElement("a");
  a.href = url;
  const stem = (subject || "").replace(/[\\/:*?"<>|]+/g, " ").replace(/\s+/g, " ").trim().slice(0, 80);
  a.download = `${stem || "message"}.eml`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.setTimeout(() => URL.revokeObjectURL(url), 1000);
}

/** Browser download link (server forces Content-Disposition: attachment).
 *  Same ?token= contract as attachmentDownloadUrl. */
export function mailAttachmentDownloadUrl(id: string): string {
  const base = api.defaults.baseURL ?? "";
  const token = useAuthStore.getState().accessToken;
  const qs = token ? `?token=${encodeURIComponent(token)}` : "";
  return `${base}/mail/attachments/${id}/download${qs}`;
}


// --- Folders ------------------------------------------------------------------

export interface MailFolderRow {
  id: string;
  name: string;
  /** Belongs to a mailbox (false = your personal folder). */
  shared: boolean;
  mailboxId: string | null;
  mailboxAddress: string | null;
  /** Kind of that mailbox; only a "shared" mailbox's folder is called shared. */
  mailboxKind: MailboxKind | null;
  /** False = another mailbox's folder opened through the read-every-mailbox
   *  right (SA / TA): you can open it, not file into it or change it. */
  canUse: boolean;
  status: "active" | "inactive";
}

/** A folder of a team (shared) mailbox, seen by all its members. */
export function isTeamFolder(f: Pick<MailFolderRow, "shared" | "mailboxKind">): boolean {
  return f.shared && f.mailboxKind === "shared";
}

/** Where a folder lives, in words: "Personal folder", "Shared · accounts@…",
 *  or just the address for a personal mailbox's folder. */
export function folderPlace(f: Pick<MailFolderRow, "shared" | "mailboxKind" | "mailboxAddress">): string {
  if (!f.shared) return "Personal folder";
  return isTeamFolder(f) ? `Shared · ${f.mailboxAddress ?? ""}` : f.mailboxAddress ?? "";
}

export function useMailFolders(enabled = true) {
  return useQuery<{ results: MailFolderRow[] }>({
    queryKey: ["mail", "folders"],
    queryFn: () => api.get<{ results: MailFolderRow[] }>("/mail/folders").then((r) => r.data),
    enabled,
  });
}

export function useCreateFolder() {
  const qc = useQueryClient();
  return useMutation<MailFolderRow, unknown, { name: string; mailboxId?: string | null }>({
    mutationFn: (body) => api.post<MailFolderRow>("/mail/folders", body).then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["mail", "folders"] }),
  });
}

export function useRenameFolder() {
  const qc = useQueryClient();
  return useMutation<MailFolderRow, unknown, { id: string; name: string }>({
    mutationFn: ({ id, name }) => api.patch<MailFolderRow>(`/mail/folders/${id}`, { name }).then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["mail"] }),
  });
}

export function useDeactivateFolder() {
  const qc = useQueryClient();
  return useMutation<MailFolderRow, unknown, string>({
    mutationFn: (id) => api.post<MailFolderRow>(`/mail/folders/${id}/deactivate`, {}).then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["mail"] }),
  });
}

export function useMoveToFolder(threadId: string) {
  return useThreadMutation<{ folderId: string }>(threadId, "folder", (v) => v);
}

// --- Contacts -------------------------------------------------------------------

export interface MailContact {
  address: string;
  name: string;
  kind: "staff" | "client" | "mailbox" | "recent";
  /** Client name for client contact persons. */
  detail: string;
}

export function searchMailContacts(search: string): Promise<MailContact[]> {
  return api
    .get<{ results: MailContact[] }>("/mail/contacts", { params: { search: search || undefined } })
    .then((r) => r.data.results);
}

// --- Rules ------------------------------------------------------------------------

export type MailRuleAction = "link_workunit" | "create_workunit" | "not_work";
export type MailRuleHitOutcome = "applied" | "failed" | "capped";

export interface MailRuleInput {
  name: string;
  mailboxId: string | null;
  position: number;
  fromContains: string;
  senderClientId: string | null;
  toContains: string;
  subjectContains: string;
  bodyContains: string;
  hasAttachment: boolean | null;
  action: MailRuleAction;
  workUnitId: string | null;
  projectId: string | null;
  clientSource: "sender" | "fixed";
  clientId: string | null;
  reuseOpenWorkUnit: boolean;
  dueInDays: number;
  notWorkReason: string;
  folderId: string | null;
  maxPerHour: number;
  /** Sender conditions count only when the receiving server confirmed the sender. */
  verifiedSenderOnly: boolean;
}

export interface MailRuleRow extends MailRuleInput {
  id: string;
  mailboxAddress: string | null;
  senderClientName: string | null;
  workUnitCtrlNo: string | null;
  projectName: string | null;
  clientName: string | null;
  folderName: string | null;
  matchCount: number;
  lastMatchedAt: string | null;
  status: "active" | "inactive";
  authorName: string;
  updatedAt: string | null;
  recentHits?: {
    threadId: string;
    outcome: MailRuleHitOutcome;
    detail: string;
    createdAt: string;
    workUnitCtrlNo: string | null;
  }[];
}

export function useMailRules() {
  return useQuery<{ results: MailRuleRow[] }>({
    queryKey: ["mail", "rules"],
    queryFn: () => api.get<{ results: MailRuleRow[] }>("/mail/rules").then((r) => r.data),
  });
}

export function useMailRule(id: string | null) {
  return useQuery<MailRuleRow>({
    queryKey: ["mail", "rules", "detail", id],
    queryFn: () => api.get<MailRuleRow>(`/mail/rules/${id}`).then((r) => r.data),
    enabled: !!id,
  });
}

export function useSaveRule(id: string | null) {
  const qc = useQueryClient();
  return useMutation<MailRuleRow, unknown, Partial<MailRuleInput>>({
    mutationFn: (body) =>
      (id ? api.patch<MailRuleRow>(`/mail/rules/${id}`, body) : api.post<MailRuleRow>("/mail/rules", body)).then(
        (r) => r.data,
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["mail", "rules"] }),
  });
}

export function useSetRuleActive() {
  const qc = useQueryClient();
  return useMutation<MailRuleRow, unknown, { id: string; active: boolean }>({
    mutationFn: ({ id, active }) =>
      api.post<MailRuleRow>(`/mail/rules/${id}/${active ? "activate" : "deactivate"}`, {}).then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["mail", "rules"] }),
  });
}

export function testMailRule(body: Partial<MailRuleInput>) {
  return api
    .post<{ days: number; count: number; samples: { threadId: string; subject: string; from: string; sentAt: string | null }[] }>(
      "/mail/rules/test",
      body,
    )
    .then((r) => r.data);
}

// --- WorkUnit Documents tab ------------------------------------------------------------

export interface WorkUnitMailFile extends MailAttachmentRow {
  messageId: string;
  threadId: string;
  subject: string;
  from: MailAddress;
  sentAt: string | null;
}

export function useWorkUnitMailFiles(workUnitId: string, enabled = true) {
  return useQuery<{ results: WorkUnitMailFile[] }>({
    queryKey: ["mail", "work-unit-files", workUnitId],
    queryFn: () =>
      api.get<{ results: WorkUnitMailFile[] }>(`/mail/work-units/${workUnitId}/attachments`).then((r) => r.data),
    enabled: enabled && !!workUnitId,
  });
}


// --- WorkUnit suggestions / rule mailboxes ----------------------------------------------

export interface WorkUnitSuggestion {
  workUnit: {
    id: string;
    ctrlNo: string;
    clientName: string;
    projectName: string;
    description: string;
    currentWorkflowName: string;
    financialYear: string;
    dueAt: string | null;
    lifecycleState: string;
  };
  score: number;
  /** How sure the evidence is. */
  confidence: "strong" | "likely" | "possible";
  /** Why it is suggested (plain sentences). */
  reasons: string[];
}

export function useThreadSuggestions(threadId: string, enabled = true) {
  return useQuery<{ results: WorkUnitSuggestion[] }>({
    queryKey: ["mail", "threads", "suggestions", threadId],
    queryFn: () =>
      api.get<{ results: WorkUnitSuggestion[] }>(`/mail/threads/${threadId}/suggestions`).then((r) => r.data),
    enabled: enabled && !!threadId,
    staleTime: 60_000,
  });
}

export function useRuleMailboxes() {
  return useQuery<{ results: { id: string; address: string; displayName: string }[] }>({
    queryKey: ["mail", "rules", "mailboxes"],
    queryFn: () =>
      api.get<{ results: { id: string; address: string; displayName: string }[] }>("/mail/rules/mailboxes").then((r) => r.data),
  });
}


// --- Who can read which mail (Technical Admin) / sidebar count ---------------------------

export interface MailAccessRole {
  role: string;
  label: string;
  /** Reads every mailbox. */
  readAll: boolean;
  /** The Technical Admin's read-all cannot be switched off. */
  readAllLocked: boolean;
  /** Reads the mail filed under WorkUnits they can see. */
  workUnit: boolean;
}

export function useMailAccessPolicy(enabled = true) {
  return useQuery<{ canEdit: boolean; roles: MailAccessRole[] }>({
    queryKey: ["mail", "access-policy"],
    queryFn: () => api.get<{ canEdit: boolean; roles: MailAccessRole[] }>("/mail/access-policy").then((r) => r.data),
    enabled,
  });
}

export function useSaveMailAccessPolicy() {
  const qc = useQueryClient();
  return useMutation<{ canEdit: boolean; roles: MailAccessRole[] }, unknown, { readAll: string[]; workUnit: string[] }>({
    mutationFn: (body) =>
      api.put<{ canEdit: boolean; roles: MailAccessRole[] }>("/mail/access-policy", body).then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["mail"] }),
  });
}

/** Unread conversations in your Inbox (sidebar badge). */
export function useMailUnreadCount(enabled = true) {
  return useQuery<number>({
    queryKey: ["mail", "threads", "unread-count"],
    queryFn: () =>
      api
        .get<{ count: number }>("/mail/threads", { params: { scope: "mine", unread: 1, pageSize: 1 } })
        .then((r) => r.data.count),
    enabled,
    refetchInterval: 60_000,
  });
}
